import React, { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import './SplitTipScreen.css';
import Page from 'components/Page';
import SplitMethodSelector from './SplitMethodSelector';
import { API } from 'common';
import _, { first, sumBy } from 'lodash';
import { toastRef } from 'components/Toast';

const SplitTipScreen = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [totalTip, setTotalTip] = useState(100); // Default to 100 if not provided
  const [splitMethod, setSplitMethod] = useState('percent'); // 'percent', 'even', 'manual'
  const [staffMembers, setStaffMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPayment = async () => {
    setIsLoading(true);
    try {
      const res = await API.get(`/report/getBillDetailV2?billId=${ticketId}`);
      const detail = res.data.payload;
      if (detail) {
        const staff = _.chain(detail?.services || [])
          .groupBy("staffId")?.map((services, key) => {
            const o = first(services);
            return ({
              staffId: key,
              id: o.staffId,
              name: o.technician,
              avatar: '',
              services: services.map(ser => {
                return ({
                  name: ser?.serviceName, price: o.price,
                });
              })
            })
          }).value();
        setStaffMembers(staff.map(o => ({
          id: o.staffId, name: o.name, serviceAmount: sumBy(o.services, s => s.price), tipAmount: 0
        })));
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  }
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const amount = parseFloat(searchParams.get('amount') || 100);
    setTotalTip(amount);
    fetchPayment();
  }, [ticketId, location.search]);

  useEffect(() => {
    distributeStaffTips();
  }, [splitMethod, totalTip, staffMembers.length]);

  const distributeStaffTips = () => {
    if (staffMembers.length === 0) return;

    if (splitMethod === 'percent') {
      const totalServiceAmount = staffMembers.reduce(
        (sum, staff) => sum + staff.serviceAmount,
        0
      );
      const updatedStaff = staffMembers.map((staff) => ({
        ...staff,
        tipAmount: parseFloat(
          ((staff.serviceAmount / totalServiceAmount) * totalTip).toFixed(2)
        ),
      }));
      setStaffMembers(updatedStaff);
    } else if (splitMethod === 'even') {
      const evenTip = parseFloat((totalTip / staffMembers.length).toFixed(2));

      const updatedStaff = staffMembers.map((staff, index) => {
        if (index === staffMembers.length - 1) {
          const previousTipsSum = staffMembers
            .slice(0, -1)
            .reduce(
              (sum, s) =>
                sum + parseFloat((totalTip / staffMembers.length).toFixed(2)),
              0
            );

          const adjustedTip = parseFloat(
            (totalTip - previousTipsSum).toFixed(2)
          );
          return {
            ...staff,
            tipAmount: adjustedTip,
          };
        }
        return {
          ...staff,
          tipAmount: evenTip,
        };
      });

      setStaffMembers(updatedStaff);
    }
  };

  const handleSplitMethodChange = (method) => {
    setSplitMethod(method);
  };

  const handleStaffTipChange = (staffId, value) => {
    let newValue = value.replace(/[^0-9.]/g, '');

    const decimalCount = (newValue.match(/\./g) || []).length;
    if (decimalCount > 1) {
      const firstDecimalIndex = newValue.indexOf('.');
      newValue =
        newValue.substring(0, firstDecimalIndex + 1) +
        newValue.substring(firstDecimalIndex + 1).replace(/\./g, '');
    }

    const numValue = parseFloat(newValue) || 0;

    const updatedStaffMembers = staffMembers.map((staff) =>
      staff.id === staffId ? { ...staff, tipAmount: numValue } : staff
    );

    setStaffMembers(updatedStaffMembers);
  };

  const calculateTotalAssignedTips = () => {
    return staffMembers.reduce(
      (sum, staff) => sum + parseFloat(staff.tipAmount || 0),
      0
    );
  };

  const validateTips = () => {
    const totalAssigned = calculateTotalAssignedTips();
    return Math.abs(totalAssigned - totalTip) < 0.02; // Allow for small rounding errors
  };
  const handleSaveTip = async () => {
    if (!validateTips()) {
      alert(
        `Total assigned tips (${calculateTotalAssignedTips().toFixed(
          2
        )}) must equal total tip amount (${totalTip.toFixed(2)})`
      );
      return;
    }
    setIsLoading(true);
    try {
      const res = await API.put('bill/updateTipStaff', {
        payload: {
          billId: ticketId,
          totalTip,
          data: staffMembers.map(o => ({
            staffId: o.id,
            tipAmount: parseFloat(o.tipAmount),
          }))
        }
      });
      if (res.data.payload) {
        if (toastRef.current) toastRef.current.success('Success');
        history.replace(`/ticketDetailV2/${ticketId}`);
      }
    } catch (error) {
      console.error('Error saving tip:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    history.goBack();
  };

  const StickFooter = () => {
    return (
      <div className='split-tip-footer'>
        <button className='split-tip-cancel-btn' onClick={handleBack}>
          Cancel
        </button>
        <button className='split-tip-continue-btn' onClick={handleSaveTip}>
          Split Tip
        </button>
      </div>
    )
  };

  return (
    <Page loading={isLoading} title='Split Tip' className='pb-0' StickFooter={StickFooter}>
      <div className='split-tip-container'>
        <div className='split-tip-content'>
          <div className='split-tip-header'>
            <p className='split-tip-title'>Total Tip</p>
            <h2 className='split-tip-amount'>${totalTip.toFixed(2)}</h2>
          </div>
          <div className='bg-white my-2'>
            <div className='split-tip-split-status'>
              <p>Status</p>
              <SplitMethodSelector
                selectedMethod={splitMethod}
                onMethodChange={handleSplitMethodChange}
              />
            </div>

            {isLoading ? (
              <div className='split-tip-loading'>Loading staff data...</div>
            ) : (
              <div className='split-tip-staff-list'>
                {staffMembers.map((staff) => (
                  <div key={staff.id} className='split-tip-staff-item'>
                    <div className='split-tip-staff-info'>
                      <div className='split-tip-staff-avatar'>
                        <img
                          src={`https://randomuser.me/api/portraits/women/${staff.id}.jpg`} //TODO: Update avatar
                          alt={staff.name}
                        />
                      </div>
                      <div className='split-tip-staff-details'>
                        <h3>{staff.name}</h3>
                        <p>${staff.serviceAmount.toFixed(2)}</p>
                      </div>
                    </div>
                    <div className='split-tip-input-container'>
                      {splitMethod === 'manual' ? (
                        <input
                          type='text'
                          value={staff.tipAmount}
                          onChange={(e) =>
                            handleStaffTipChange(staff.id, e.target.value)
                          }
                          className='split-tip-input'
                        />
                      ) : (
                        <div className='split-tip-amount-display'>
                          $
                          {typeof staff.tipAmount === 'number'
                            ? staff.tipAmount.toFixed(2)
                            : '0.00'}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Page>
  );
};

export default SplitTipScreen;
